/**
 * Quoting contract-priced lines straight to the customer — no RFQ, no supplier quotation, no
 * comparison.
 *
 * ---- Why this path exists ----
 *
 * A blanket order fixes the rate with a named supplier in advance. A line covered by one has
 * nothing to compete: asking suppliers to quote a price that is already agreed wastes days and
 * produces documents nobody reads. Most provision enquiries are entirely contract-priced, so this
 * is the ORDINARY route to a customer quotation, not a shortcut around one.
 *
 * The old route was the only route: `ComparePage` → `buildQuotationPayload` → `create_quotation`.
 * That function begins by filtering `allocations` and refuses outright when none are allocated,
 * and an allocation only exists where a supplier quoted. So contract lines skipped the RFQ
 * correctly and then vanished — the quote screen's `provision` tag rendered lines nothing could
 * produce. This closes that.
 *
 * ---- What it shares with the allocation path, deliberately ----
 *
 * The same margin resolution (`marginFor`), the same rounding, the same line shape, and the same
 * rule that the margin is applied in the company's currency BEFORE conversion so the percentage
 * means what it says whatever the customer is billed in. Two ways of pricing one quotation would
 * drift, and the drift would be invisible until a customer compared two documents.
 *
 * ---- What it refuses ----
 *
 * A contract-routed line with no rate is NOT priced. That is a line whose contract was selected
 * but which the blanket order does not actually cover, and quoting it at zero would send the
 * customer a free item.
 */

import { printedDescription } from './internalNotes'
import { sellFromMargin } from './pricing'
import { IN_HOUSE_SUPPLIER } from './directPricing'
import { marginFor, type QuotationLinePayload } from './quotationFromAllocation'
import type { EnquiryItem, PricingRulesSettings } from './types'

const round2 = (n: number): number => Math.round(n * 100) / 100

/**
 * Contract-routed enquiry lines → priced quotation lines.
 *
 * `exchangeRate` converts the sell price into the customer's currency; pass `1` to quote in the
 * company's own. `nameByCode` supplies the catalogue name — the code stands in when the master is
 * not loaded, so a missing name never blocks a quotation.
 */
/**
 * Whether `buildContractQuotationLines` will emit this line — the single rule two screens rely on.
 *
 * `ComparePage` appends the contract lines to the allocation payload, and that payload must not
 * ALSO zero-fill them (`QuotableLine.pricedByContract`); otherwise the item reaches the customer
 * twice, once at 0.00. The two decisions have to be the same decision, so both read this rather
 * than each restating the conditions below.
 */
export function isContractPriced(line: EnquiryItem): boolean {
  return line.route === 'contract' && !!line.item_code && round2(line.selected_rate ?? 0) > 0
}

export function buildContractQuotationLines(
  lines: EnquiryItem[],
  nameByCode: ReadonlyMap<string, string>,
  settings: PricingRulesSettings,
  exchangeRate: number,
): QuotationLinePayload[] {
  const out: QuotationLinePayload[] = []
  for (const line of lines) {
    if (line.route !== 'contract') continue
    if (!line.item_code) continue
    const cost = round2(line.selected_rate ?? 0)
    // No rate means the selected contracts do not actually cover this item — see the header.
    if (cost <= 0) continue

    /*
      ---- EVERY line is margined, our own stock included ----

      This used to special-case in-house stock to a zero margin, on the reasoning that a catalogue
      `STANDARD SELLING PRICE` is already a selling price and marking it up would double-count.
      That was wrong (corrected by Akhil, 2026-09-03): the in-stock figure we hold is a COST, and
      the selling price is decided on the customer quotation page where the margin rules apply.

      So there is no special case left. `IN_HOUSE_SUPPLIER` still travels on the line, but it now
      records only WHERE the goods come from — never how they are priced. The old rule sold every
      in-house line at cost, and tripped the point-25 send gate by construction.

      Category is absent on this bench (Q15), so this resolves to `default_markup_pct` — the same
      number the allocation path lands on for the same reason.
    */
    const margin = marginFor(undefined, settings)
    // Not a Supplier docname — see `lowest_quote_supplier` below.
    const inHouse = line.suppliers[0] === IN_HOUSE_SUPPLIER
    const sell = round2(sellFromMargin({ amount: cost, currency: '' }, margin).amount * exchangeRate)
    // In the quotation's currency, like the sell — the server derives sell from cost × margin
    // (measured 2026-09-14; `quotationCurrency.ts`). The contracted rate itself stays in
    // `po_currency_rate`.
    const quoteCost = round2(cost * exchangeRate)

    out.push({
      item_code: line.item_code,
      item_name: nameByCode.get(line.item_code) || line.item_code,
      // What prints under the name — the RFQ page's edit, else the customer's own words.
      description: printedDescription(line),
      // `cust_sno` IS the enquiry row number — the through-key back to the enquiry. Never
      // `line_id`, which is the child docname and means nothing downstream.
      enquiry_row_no: line.cust_sno,
      /*
        Akhil's vocabulary: `supplier` | `provision` | `base price`. In-house stock used to be
        written as `provision`, which reads as a contract line and would have the ERP look for a
        supplier to raise a purchase order against. There is nobody to buy it from — it is already
        on our shelf.
      */
      source: inHouse ? 'Base Price' : 'Provision',
      qty: line.qty,
      uom: line.uom,
      sell_uom: line.uom,
      cost_unit: quoteCost,
      default_margin_pct: margin,
      margin,
      sell_unit: sell,
      unit_price: sell,
      amount: round2(sell * line.qty),
      selected_rate: quoteCost,
      /*
        The contracted rate and its supplier, in the fields the quote screen reads as "what was
        available". For an RFQ line these mean "the cheapest offer we received"; for a contract
        line there is exactly one price and one supplier, which is the same fact with no
        competition behind it.

        ---- EMPTY for in-house stock, and it has to be ----

        `lowest_quote_supplier` is `Link → Supplier` on both `Quotation Item` and `Quotation Line`
        (queried live). `IN_HOUSE_SUPPLIER` is a marker this codebase invented; no such Supplier
        document exists, so sending it failed Frappe's link validation and took the WHOLE
        quotation down with it:

            Could not find Row #1: Lowest Quote Supplier: In-house stock, Row #2: …

        Empty is also the honest answer, not merely the working one: "which supplier quoted us the
        lowest price" has no answer for goods off our own shelf. Nobody quoted. The cost is still
        on the line and `source` still records where it came from.

        `rfq.ts` guards the same marker out of RFQ generation for the same reason — it is a
        display label, never an id, and CLAUDE.md's most-repeated bug is exactly this.
      */
      lowest_quote_price: cost,
      lowest_quote_supplier: inHouse ? '' : (line.suppliers[0] ?? ''),
      /*
        Where the goods actually came from, in the free-text field, since the Link above cannot
        hold it. Without this, blanking the link would silently lose the one fact that separates
        "priced from a contract" from "taken off our own shelf" — the quotation would show neither
        a supplier nor a reason.
      */
      ...(inHouse ? { supplier_note: IN_HOUSE_SUPPLIER } : {}),
      /*
        The PO columns (`D22`). A contract line is bought from the blanket-order supplier at the
        contracted rate; an in-house line is bought from nobody, so `supplier` is EMPTY — the
        `In-house stock` marker is not a Supplier, and this is a Link. Exactly the trap that broke
        `lowest_quote_supplier` above.
      */
      supplier: inHouse ? '' : (line.suppliers[0] ?? ''),
      po_currency_rate: cost,
      // `po_currency_rate × conversion_rate = cost_unit` — the contracted rate is in the
      // company's currency, so this is the company → quotation rate.
      conversion_rate: exchangeRate,
    })
  }
  return out
}

/** What a contract quotation would come to, before tax or commission. */
export const contractLinesTotal = (items: QuotationLinePayload[]): number =>
  round2(items.reduce((sum, i) => sum + i.amount, 0))

/**
 * EVERY enquiry line, for the RFQ builder's quotation — priced where the contract builder priced
 * it, at ZERO otherwise, in the customer's own order.
 *
 * Akhil, 2026-09-15: a quotation built from the RFQ builder used to carry only the contract and
 * stock-priced lines, so a 40-line list reached the customer as the six that had a price. The
 * customer's list must read whole; the purchaser prices the zeros on the quotation page — the
 * same shape `buildQuotationPayload` gives a line nobody allocated on the comparison.
 *
 * A line with no item code is skipped: `Quotation Line.item_code` is a Link, and a row naming
 * nothing fails the whole document. Those stay on the RFQ builder as "unmatched".
 */
export function everyEnquiryLine(
  lines: readonly EnquiryItem[],
  priced: readonly QuotationLinePayload[],
  nameByCode: ReadonlyMap<string, string>,
  settings: PricingRulesSettings,
): QuotationLinePayload[] {
  const pricedByRow = new Map(priced.map((row) => [row.enquiry_row_no, row]))
  const out: QuotationLinePayload[] = []
  for (const line of lines) {
    const already = pricedByRow.get(line.cust_sno)
    if (already) {
      out.push(already)
      continue
    }
    if (!line.item_code) continue
    const margin = marginFor(undefined, settings)
    out.push({
      item_code: line.item_code,
      item_name: nameByCode.get(line.item_code) || line.item_code,
      description: printedDescription(line),
      enquiry_row_no: line.cust_sno,
      source: 'Supplier',
      qty: line.qty,
      uom: line.uom,
      sell_uom: line.uom,
      cost_unit: 0,
      default_margin_pct: margin,
      margin,
      sell_unit: 0,
      unit_price: 0,
      amount: 0,
      selected_rate: 0,
      lowest_quote_price: 0,
      // A Link — empty, never invented (`D22`).
      lowest_quote_supplier: '',
    })
  }
  return out
}
